/**
 * GlobalChrome — app-wide chrome mounted once in App.tsx so every screen
 * gets it automatically without editing 30 pages:
 *
 *   • Breadcrumb strip — fixed top bar showing "Home > Section > Page"
 *     so the user always knows where they are and can jump back.
 *   • Back FAB - bottom-left floating button labeled plain "Back". It
 *     calls browser history back (falling to the parent breadcrumb route
 *     only when there is no history to go back to), so the label names
 *     what it does instead of promising a fixed destination like "Home"
 *     that a non-linear path (a notification deep link, a jump from
 *     search) would not actually land on.
 *   • Jump-to-top FAB — bottom-right, visible after 400 px of scroll.
 *
 * Wayfinding philosophy: never let a user wonder where they are or how to
 * get back. Every level of hierarchy is named.
 */

import { Suspense, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from './Icon';
import AssistantLauncher from './AssistantLauncher';
import { useVersionFreshness } from '../lib/freshness';
import { markReload, offerHeldReload } from '../lib/reloadGuard';
import { lazyRoute, preloadChunk } from '../lib/lazyRoute';
import OptionalFeatureBoundary from './OptionalFeatureBoundary';
import { useTheme } from '../lib/theme';
import { useAuth } from '../lib/auth-context';
import { useUserDoc } from '../lib/users';
import { canOpenAttendancePage } from '../lib/roleCapabilities';
import { useMustChangePassword } from '../lib/tempAccounts';
import ForcePasswordChange from './ForcePasswordChange';
import { usePreviewRole, setPreviewRole, PREVIEW_ROLE_LABEL } from '../lib/previewRole';
import { useGlobalFabsHidden } from '../lib/globalFabVisibility';
import { useFabViewportTracking } from '../lib/fabViewportTracking';
import { useViewportResync } from '../lib/viewportResync';
import ScreenCheckReadout from './ScreenCheckReadout';
import { FAMILY_PREVIEW_ROUTES, setPreviewedFamily } from '../lib/demoStudio';
import WelcomeTour from './WelcomeTour';
import { isCostumePickupPath } from '../lib/returnTo';

// THE MENU DRAWER LOADS AFTER THE FIRST SCREEN (speed plan 2026-10-19, P2;
// audit A-13: about 107 KB of source on the startup path for a panel nobody
// sees until they tap the menu). Its chunk is FETCHED ahead of need by
// preloadChunk() once the first screen is up, and the drawer is only put in
// the tree once that fetch has landed or the menu has been tapped.
//
// Why the two steps (refute round, 2026-10-25). The drawer used to mount on
// every signed-in page straight through lazyRoute(), so a chunk that would
// not load (an offline open, a deploy that replaced it) reloaded the whole
// Hub with nobody waiting for the menu and no unsaved-work check, then spent
// the 60 second reload cooldown. preloadChunk() never reloads: a failed
// fetch just leaves the drawer out of the tree. Only a TAP mounts it through
// lazyRoute(), and a person who tapped the menu IS waiting for it, so a stale
// chunk then gets the Hub's one automatic reload like any page; if even that
// cannot recover, the OptionalFeatureBoundary around it keeps the rest of the
// Hub on screen. A tap before the chunk lands mounts the drawer already
// open, which NavDrawer's history block handles (its wasOpenRef comment).
const loadNavDrawer = () => import('./NavDrawer');
const NavDrawer = lazyRoute(loadNavDrawer);

// FAMILY_PREVIEW_ROUTES lives in lib/demoStudio.ts, not here - it is also
// the set FamilyCommandCenter.tsx's own read-only lock carves its two
// walkable doors out of (goTo / onLockedAnchor / SourceButton), and a
// second copy here would let the two drift apart. See its doc comment
// there for the reasoning.

/**
 * Height of the "view as" banner; other fixed top chrome offsets by this.
 * Two lines tall (the role + Leave preview on one row, the honest-limit
 * sentence below it), so taller than a single text row would need.
 */
const PREVIEW_BANNER_H = 60;
/** Height of the fixed breadcrumb strip (matches BreadcrumbStrip's height). */
const BREADCRUMB_H = 36;
/**
 * Clearance reserved at the very bottom of every page so the floating back
 * FAB and jump-to-top FAB never sit on top of a page's own bottom action
 * row (a "Create" / "Send" button, a message composer, etc). Bobbie hit
 * this as a real blocker: the back FAB covered the New Task submit button
 * and a message thread's composer input.
 *
 * Sized to the tallest FAB (the 48px round jump-to-top button), plus the
 * FABs' own bottom inset (14px), plus a small buffer (8px) so a tap near
 * the top edge of a FAB does not also land on page content right above it:
 * 14 + 48 + 8 = 70px. This is on top of env(safe-area-inset-bottom), same
 * as the FABs' own bottom offset, so it lines up exactly with where the
 * FABs actually sit rather than double-reserving safe-area space.
 *
 * This is one shared reservation for the whole bottom FAB band. 70 was
 * measured against the back FAB alone and was wrong in practice: the right
 * side stacks TWO controls (the assistant chat bubble above the jump-to-top
 * arrow), so the band is two FAB heights plus gaps tall, and Bobbie could
 * not reach the last card on any page - the Full schedule link, class
 * notes, and the music list all sat underneath the pills with no way to
 * scroll them clear (three screenshots, 2026-07-28). 140 clears the full
 * stack. It only shows as blank space at the very end of a scroll, so
 * generous beats blocked.
 */
const FAB_CLEARANCE_H = 140;

// ---------------------------------------------------------------------------
// Route map — derive breadcrumb segments + back-button label from the path
// ---------------------------------------------------------------------------

export interface Crumb {
  label: string;
  href: string;
}

/**
 * Returns breadcrumb segments for the given pathname. The last segment is
 * the current page; earlier segments are tappable parent links.
 */
function buildCrumbs(pathname: string): Crumb[] {
  // Exact matches first, then prefix patterns (order matters)
  const exact: Record<string, Crumb[]> = {
    '/': [{ label: 'Home', href: '/' }],
    '/teach': [{ label: 'Home', href: '/' }, { label: 'Teach', href: '/teach' }],
    '/solo-music': [{ label: 'Home', href: '/' }, { label: 'Solo Music', href: '/solo-music' }],
    '/all-music': [{ label: 'Home', href: '/' }, { label: 'All Music', href: '/all-music' }],
    '/teach/classes': [{ label: 'Home', href: '/' }, { label: 'Teach', href: '/teach' }, { label: 'Your Classes', href: '/teach/classes' }],
    '/messages': [{ label: 'Home', href: '/' }, { label: 'Messages', href: '/messages' }],
    '/interns': [{ label: 'Home', href: '/' }, { label: 'Interns', href: '/interns' }],
    '/executive-board': [{ label: 'Home', href: '/' }, { label: 'Directors + Managers + Executive Teachers', href: '/executive-board' }],
    '/command-center': [{ label: 'Home', href: '/' }, { label: 'Command Center', href: '/command-center' }],
    '/website/analytics': [{ label: 'Home', href: '/' }, { label: 'Website analytics', href: '/website/analytics' }],
    '/command-center/big-little': [{ label: 'Home', href: '/' }, { label: 'Command Center', href: '/command-center' }, { label: 'Big/Little Matcher', href: '/command-center/big-little' }],
    '/big-little': [{ label: 'Home', href: '/' }, { label: 'Big/Little Board', href: '/big-little' }],
    '/messages/new': [{ label: 'Home', href: '/' }, { label: 'Messages', href: '/messages' }, { label: 'New', href: '/messages/new' }],
    '/tasks': [{ label: 'Home', href: '/' }, { label: 'To-dos', href: '/tasks' }],
    '/tasks/new': [{ label: 'Home', href: '/' }, { label: 'To-dos', href: '/tasks' }, { label: 'New to-do', href: '/tasks/new' }],
    '/bulletins': [{ label: 'Home', href: '/' }, { label: 'Announcements', href: '/bulletins' }],
    '/subs': [{ label: 'Home', href: '/' }, { label: 'Sub Requests', href: '/subs' }],
    '/attendance': [{ label: 'Home', href: '/' }, { label: 'Attendance', href: '/attendance' }],
    '/attendance/concerns': [{ label: 'Home', href: '/' }, { label: 'Attendance', href: '/attendance' }, { label: 'Concerns', href: '/attendance/concerns' }],
    '/attendance/report': [{ label: 'Home', href: '/' }, { label: 'Attendance', href: '/attendance' }, { label: 'Report', href: '/attendance/report' }],
    '/comp-reports': [{ label: 'Home', href: '/' }, { label: 'Comp alternates & attendance', href: '/comp-reports' }],
    '/eval-times': [{ label: 'Home', href: '/' }, { label: 'Eval times', href: '/eval-times' }],
    '/calendar': [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/calendar' }],
    '/events': [{ label: 'Home', href: '/' }, { label: 'Events', href: '/events' }],
    '/calendar/new': [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/calendar' }, { label: 'New Reservation', href: '/calendar/new' }],
    '/calendar/search': [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/calendar' }, { label: 'Find Availability', href: '/calendar/search' }],
    '/calendar/requests': [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/calendar' }, { label: 'Room Requests', href: '/calendar/requests' }],
    '/calendar/grid': [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/calendar' }, { label: 'Schedule Grid', href: '/calendar/grid' }],
    '/calendar/closures': [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/calendar' }, { label: 'Closures', href: '/calendar/closures' }],
    '/calendar/open-solos': [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/calendar' }, { label: 'Open solo times', href: '/calendar/open-solos' }],
    '/calendar/import': [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/calendar' }, { label: 'Import Schedule', href: '/calendar/import' }],
    '/rehearsals': [{ label: 'Home', href: '/' }, { label: 'Rehearsals', href: '/rehearsals' }],
    '/recital-builder': [{ label: 'Home', href: '/' }, { label: 'Recital Builder', href: '/recital-builder' }],
    '/rooms': [{ label: 'Home', href: '/' }, { label: 'Rooms', href: '/rooms' }],
    '/offerings': [{ label: 'Home', href: '/' }, { label: 'Classes', href: '/offerings' }],
    // Both /students and its /dancers alias read "Dancers" in the crumb -
    // Bobbie calls them dancers, /students only survives for old bookmarks.
    '/students': [{ label: 'Home', href: '/' }, { label: 'Dancers', href: '/dancers' }],
    '/students/import': [{ label: 'Home', href: '/' }, { label: 'Dancers', href: '/dancers' }, { label: 'Import', href: '/students/import' }],
    '/dancers': [{ label: 'Home', href: '/' }, { label: 'Dancers', href: '/dancers' }],
    '/injuries': [{ label: 'Home', href: '/' }, { label: 'Injuries', href: '/injuries' }],
    '/dancers/import': [{ label: 'Home', href: '/' }, { label: 'Dancers', href: '/dancers' }, { label: 'Import', href: '/dancers/import' }],
    '/registrations': [{ label: 'Home', href: '/' }, { label: 'Registrations', href: '/registrations' }],
    '/payroll': [{ label: 'Home', href: '/' }, { label: 'Subbing report', href: '/payroll' }],
    '/staff-activity': [{ label: 'Home', href: '/' }, { label: 'Staff activity', href: '/staff-activity' }],
    '/upload-speed': [{ label: 'Home', href: '/' }, { label: 'Upload speed', href: '/upload-speed' }],
    '/removed-posts': [{ label: 'Home', href: '/' }, { label: 'Removed posts', href: '/removed-posts' }],
    '/register': [{ label: 'Home', href: '/' }, { label: 'Register', href: '/register' }],
    '/notifications': [{ label: 'Home', href: '/' }, { label: 'Notifications', href: '/notifications' }],
    '/account': [{ label: 'Home', href: '/' }, { label: 'Account', href: '/account' }],
    '/staff': [{ label: 'Home', href: '/' }, { label: 'Staff', href: '/staff' }],
    '/manage-users': [{ label: 'Home', href: '/' }, { label: 'Manage Users', href: '/manage-users' }],
    '/pages-boards-setup': [{ label: 'Home', href: '/' }, { label: 'Pages & Boards Setup', href: '/pages-boards-setup' }],
    '/audit-log': [{ label: 'Home', href: '/' }, { label: 'Audit Log', href: '/audit-log' }],
    // No breadcrumb for /demo-studio: it is reachable only by typing the
    // URL now (Bobbie, 2026-08-13), and a crumb is a link like any other.
    '/feedback': [{ label: 'Home', href: '/' }, { label: 'App feedback', href: '/feedback' }],
    '/student-awards': [{ label: 'Home', href: '/' }, { label: 'Student Awards', href: '/student-awards' }],
    '/student-awards/certificates': [{ label: 'Home', href: '/' }, { label: 'Student Awards', href: '/student-awards' }, { label: 'Print certificates', href: '/student-awards/certificates' }],
    '/production-tracker': [{ label: 'Home', href: '/' }, { label: 'Recital/Comp Tracker', href: '/production-tracker' }],
    '/production-tracker/report': [{ label: 'Home', href: '/' }, { label: 'Recital/Comp Tracker', href: '/production-tracker' }, { label: 'Final List', href: '/production-tracker/report' }],
    '/costume-labels': [{ label: 'Home', href: '/' }, { label: 'Recital/Comp Tracker', href: '/production-tracker' }, { label: 'Costume Labels', href: '/costume-labels' }],
    '/costume-status': [{ label: 'Home', href: '/' }, { label: 'Recital/Comp Tracker', href: '/production-tracker' }, { label: 'Costume Status', href: '/costume-status' }],
    '/costume-report': [{ label: 'Home', href: '/' }, { label: 'Costume Report', href: '/costume-report' }],
    '/costume-guide': [{ label: 'Home', href: '/' }, { label: 'Costume Report', href: '/costume-report' }, { label: 'Costume how-to', href: '/costume-guide' }],
    '/costume-pickups': [{ label: 'Home', href: '/' }, { label: 'Costume Pickup Report', href: '/costume-pickups' }],
    '/costume-sizes': [{ label: 'Home', href: '/' }, { label: 'Recital/Comp Tracker', href: '/production-tracker' }, { label: 'Costume Sizes', href: '/costume-sizes' }],
    '/assistant': [{ label: 'Home', href: '/' }, { label: 'Studio Assistant', href: '/assistant' }],
    '/print/rosters': [{ label: 'Home', href: '/' }, { label: 'Print Rosters', href: '/print/rosters' }],
    '/print/schedule': [{ label: 'Home', href: '/' }, { label: 'Print Schedule', href: '/print/schedule' }],
    '/admin-view': [{ label: 'Home', href: '/' }, { label: 'Admin View', href: '/admin-view' }],
    '/family/command-center': [{ label: 'Home', href: '/' }, { label: 'Family Command Center', href: '/family/command-center' }],
    '/family/dancers': [{ label: 'Home', href: '/' }, { label: 'Dancers', href: '/family/dancers' }],
    '/family/classes': [{ label: 'Home', href: '/' }, { label: 'Classes', href: '/family/classes' }],
    '/family/calendar': [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/family/calendar' }],
    '/family/videos': [{ label: 'Home', href: '/' }, { label: 'My Videos', href: '/family/videos' }],
    '/family/music': [{ label: 'Home', href: '/' }, { label: 'My Music', href: '/family/music' }],
    '/community': [{ label: 'Home', href: '/' }, { label: 'Comp Parent Board', href: '/community' }],
    '/workshops': [{ label: 'Home', href: '/' }, { label: 'Sign Up', href: '/workshops' }],
    '/workshop-signups': [{ label: 'Home', href: '/' }, { label: 'Workshop Signups', href: '/workshop-signups' }],
    '/hub-check': [{ label: 'Home', href: '/' }, { label: 'Hub Check', href: '/hub-check' }],
    '/design-preview': [{ label: 'Home', href: '/' }, { label: 'Design Preview', href: '/design-preview' }],
    '/season-preview': [{ label: 'Home', href: '/' }, { label: 'Season Preview', href: '/season-preview' }],
    '/search': [{ label: 'Home', href: '/' }, { label: 'Search', href: '/search' }],
    // /comp redirects into the tracker's Comp dancers job, so this crumb only
    // ever flashes on the way through. Named for where it lands.
    '/comp': [{ label: 'Home', href: '/' }, { label: 'Comp dancers', href: '/comp' }],
    '/policies': [{ label: 'Home', href: '/' }, { label: 'Policies & FAQ', href: '/policies' }],
    '/faq': [{ label: 'Home', href: '/' }, { label: 'FAQ', href: '/faq' }],
  };

  if (exact[pathname]) return exact[pathname];

  // Dynamic routes — match by prefix
  if (/^\/teach\/[^/]+$/.test(pathname)) {
    return [{ label: 'Home', href: '/' }, { label: 'Teach', href: '/teach' }, { label: 'Class', href: pathname }];
  }
  if (/^\/messages\/[^/]+$/.test(pathname)) {
    return [{ label: 'Home', href: '/' }, { label: 'Messages', href: '/messages' }, { label: 'Thread', href: pathname }];
  }
  if (/^\/class\/[^/]+\/feed$/.test(pathname)) {
    return [{ label: 'Home', href: '/' }, { label: 'Class Feed', href: pathname }];
  }
  // The redesigned single class page. Ordered after the /feed pattern above so
  // the existing class feed keeps its own crumb.
  if (/^\/class\/[^/]+$/.test(pathname)) {
    return [{ label: 'Home', href: '/' }, { label: 'Class', href: pathname }];
  }
  if (/^\/offerings\/[^/]+$/.test(pathname)) {
    return [{ label: 'Home', href: '/' }, { label: 'Classes', href: '/offerings' }, { label: 'Class', href: pathname }];
  }
  if (/^\/newsletters\/[^/]+$/.test(pathname)) {
    return [{ label: 'Home', href: '/' }, { label: 'Announcements', href: '/bulletins' }, { label: 'Long read', href: pathname }];
  }
  // Parent crumb is the CALENDAR, not /rehearsals. /rehearsals stopped
  // being a page: it is a pure redirect now (RehearsalSchedules.tsx) that
  // sends staff to /calendar/import, so a teacher reading a published
  // timeline used to tap the crumb above it and land on "Import a schedule
  // - Paste Kim's rehearsal times and turn them into real bookings." The
  // rehearsal times themselves live on the calendar as real bookings, so
  // that is where "up one level" actually goes.
  //
  // buildCrumbs is pathname-only by design, so this one href cannot branch
  // on role the way RehearsalScheduleDetail's own back buttons do. A family
  // account tapping it lands on /calendar, which for a non-staff viewer
  // renders the existing "Open your family calendar" card with a one-tap
  // link - a signpost, not the dead end /rehearsals was.
  if (/^\/rehearsals\/[^/]+$/.test(pathname)) {
    return [{ label: 'Home', href: '/' }, { label: 'Calendar', href: '/calendar' }, { label: 'Timeline', href: pathname }];
  }
  // One event's own page (2026-10-18): up one level is the Events list.
  if (/^\/events\/[^/]+$/.test(pathname)) {
    return [{ label: 'Home', href: '/' }, { label: 'Events', href: '/events' }, { label: 'Event', href: pathname }];
  }
  if (/^\/family\/dancer\/[^/]+$/.test(pathname)) {
    return [{ label: 'Home', href: '/' }, { label: 'Dancers', href: '/family/dancers' }, { label: 'Profile', href: pathname }];
  }

  // Fallback
  return [{ label: 'Home', href: '/' }];
}

/**
 * Where the back FAB navigates when there is no browser history to go
 * back through (a fresh tab, a deep link). Otherwise the FAB uses
 * history back, which is the button's real, primary behavior - see
 * BackButton below.
 */
function backHref(crumbs: Crumb[]): string {
  if (crumbs.length < 2) return '/';
  return crumbs[crumbs.length - 2].href;
}

// ---------------------------------------------------------------------------
// Hamburger button — opens the site-wide NavDrawer. Lives at the left edge
// of the breadcrumb strip (or floats alone on the home screen, where the
// strip otherwise has nothing else to show).
// ---------------------------------------------------------------------------

function HamburgerIcon() {
  return (
    <svg width="19" height="14" viewBox="0 0 19 14" fill="none" aria-hidden="true">
      <rect y="0" width="19" height="2.2" rx="1.1" fill="currentColor" />
      <rect y="5.9" width="19" height="2.2" rx="1.1" fill="currentColor" />
      <rect y="11.8" width="19" height="2.2" rx="1.1" fill="currentColor" />
    </svg>
  );
}

/**
 * Shared by every 44px-tall control in the 36px strip: the hamburger, the
 * search button, the Home pill, the theme chip.
 *
 * READ THIS BEFORE BELIEVING THE BOXES ARE 44px TALL IN PRACTICE. THEY ARE
 * NOT, and an earlier version of this comment claimed they were.
 *
 * The strip sets `overflowX: hidden` with `overflowY: visible`. CSS does
 * not allow that pair: a computed `overflow-x: hidden` coerces a `visible`
 * overflow-y to `auto`. Measured `getComputedStyle(nav).overflowY ===
 * 'auto'` on this revision and on the one before it. So the nav CLIPS its
 * children at its own content box, which is 35px (36px row minus the 1px
 * bottom border), and the strip's comment about vertical overflow being
 * open has been wrong the whole time.
 *
 * Hit-testing straight down the hamburger column at x 22, every 1px, gives
 * the identical profile before and after this change: the button answers
 * from y 0 to y 34 and the page underneath answers from y 36. About 35px
 * of reachable height, against a 44px floor, either way.
 *
 * So what this constant actually does is NOT make the targets bigger. It
 * moves which 8px get clipped, from 4.5 above plus 3.5 below to 8 below,
 * and it puts the declared box where a real fix would need it: with the
 * clipping removed, a centered 44px box would poke 4.5px above the top of
 * the VIEWPORT, which no overflow rule can give back. Top-aligned, all 44
 * would be inside the page. It is a precondition, not the repair.
 *
 * Two things here ARE repairs and are measured: the theme chip below no
 * longer paints a clipped capsule, and its hit box went from 28 to 44px
 * WIDE, which the vertical clip cannot touch.
 *
 * One cost, disclosed: because the boxes now hang further past the content
 * box, the nav's scrollable overflow grew from 5px to 9px. `nav.scrollTop`
 * is settable, so an accidental drag on the strip can now shift it 9px
 * instead of 5. Nothing scrolls it deliberately.
 *
 * The real fix is to stop the nav clipping at all, which means giving it a
 * different overflow strategy than `hidden`/`visible` - the crumb trail
 * still needs its horizontal ellipsis, so this is not a one-liner. Tracked
 * as open work in TASK-QUEUE.md; do not "fix" it by nudging these boxes.
 *
 * The 8px bottom padding is not decorative. Without it the 44px box
 * centers its own contents at y 22 while the 36px row centers its
 * breadcrumb text at y 18, and the pills sit visibly 4px low against the
 * trail beside them. With it the content box is exactly the row's 36px, so
 * every painted pill lands where it always did.
 */
const stripTapTargetStyle: React.CSSProperties = {
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'flex-start',
  height: 44,
  paddingBottom: 8,
};

function HamburgerButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-label="Menu"
      aria-expanded={open}
      onClick={onToggle}
      style={{
        ...stripTapTargetStyle,
        width: 44,
        marginLeft: 'calc(-1 * var(--space-3))',
        border: 'none',
        background: 'transparent',
        color: 'var(--ink)',
        cursor: 'pointer',
        // Longhands, not `padding: 0`: index.css gives every bare button
        // horizontal padding, so this still has to zero that, but the
        // shorthand would also wipe the shared bottom padding that keeps
        // the icon centered on the row.
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      <HamburgerIcon />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Search button — the leading control in the strip
// ---------------------------------------------------------------------------

/**
 * Search entry point for the fixed chrome strip. Bobbie, 2026-07-31: "Search
 * should be first." Rendered right after the hamburger on every route (the
 * minimal home strip and the full breadcrumb strip alike) so it reads before
 * the breadcrumb trail and before the theme toggle, in both visual order and
 * DOM/tab order.
 *
 * The outer anchor DECLARES a 44x44 tap target and paints only the inner
 * 28px pill, so nothing visually pokes out of the strip. The declared box
 * is not the delivered one: the strip clips it to about 35px of reachable
 * height, which this comment used to deny. See stripTapTargetStyle above
 * for the measurement and for why that is not fixable from here.
 */
function HeaderSearchButton() {
  return (
    <a
      href="/search"
      aria-label="Search"
      style={{
        ...stripTapTargetStyle,
        width: 44,
        textDecoration: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 'var(--radius-pill)',
          color: 'var(--lime-deep)',
          background: 'var(--silver-mist)',
          border: '1px solid var(--silver-edge)',
        }}
      >
        <Icon name="search" size={16} />
      </span>
    </a>
  );
}

/**
 * Home entry point for the breadcrumb strip. Bobbie, on her phone: "Home
 * button at the top let's make it a button bigger." Before this it was
 * plain 12px breadcrumb text, the same tiny link as every other parent
 * crumb, and the only way back to the home screen from the top of a page.
 * Now it is its own pill - visibly a button (filled pill, border, bold
 * label) instead of breadcrumb text - separated out of the crumb trail so
 * it always renders first, before whatever the rest of the trail says.
 *
 * Same real-tap-target-without-growing-the-row trick as HeaderSearchButton
 * above: the outer anchor is a real 44px-tall box (the strip stays a fixed
 * 36px row; overflowY: visible on the strip lets the extra height hang off
 * the top/bottom uncropped), width is left to size itself to the visible
 * pill rather than padded out to a fixed 44px, since the pill's own text
 * plus padding already clears 44px wide on its own - padding it further
 * would just spend breadcrumb-trail budget the rest of the strip needs at
 * 320px.
 *
 * A router <Link>, not a bare <a href="/"> (follow-up to #1299, item 3). The
 * bare anchor made the browser load the whole page again on every Home tap:
 * the app booted from nothing and every in-memory scroll offset the Back
 * button relies on (lib/scrollRestore.ts) was thrown away. <Link> still
 * renders a real <a href="/"> (open in a new tab, copy link and the keyboard
 * behave as before) but a plain tap navigates inside the app. The style and
 * the 44px box are exactly what they were.
 */
function HomeCrumbButton() {
  return (
    <Link
      to="/"
      aria-label="Home"
      style={{
        ...stripTapTargetStyle,
        textDecoration: 'none',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 26,
          padding: '0 12px',
          borderRadius: 'var(--radius-pill)',
          color: 'var(--lime-deep)',
          background: 'var(--silver-mist)',
          border: '1px solid var(--silver-edge)',
          fontSize: 12,
          fontWeight: 800,
          whiteSpace: 'nowrap',
        }}
      >
        Home
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb strip — fixed top bar
// ---------------------------------------------------------------------------

interface BreadcrumbStripProps {
  crumbs: Crumb[];
  pathname: string;
  previewActive: boolean;
  showHamburger: boolean;
  menuOpen: boolean;
  onMenuToggle: () => void;
}

function BreadcrumbStrip({
  crumbs,
  pathname,
  previewActive,
  showHamburger,
  menuOpen,
  onMenuToggle,
}: BreadcrumbStripProps) {
  const onHome = crumbs.length <= 1;
  // On the home screen there are no crumbs to show. With nothing signed in
  // to open a menu either, there's nothing for this strip to do — hide it
  // entirely, same as before. Signed in, it collapses to just the hamburger
  // instead of the full crumb trail.
  if (onHome && !showHamburger) return null;
  const onSearchPage = pathname === '/search';

  return (
    <nav
      aria-label="Breadcrumb"
      // Fixed chrome, so it overlays page content on paper exactly as it
      // does on screen. This is the class theme.css has always defined and
      // this strip never wore, which is why /print/rosters, /print/schedule,
      // /costume-labels and /class/:id/feed all printed a breadcrumb bar
      // across the top of the content.
      className="no-print"
      style={{
        position: 'fixed',
        // Sit below the "view as" banner when it is showing.
        top: previewActive
          ? `calc(env(safe-area-inset-top, 0px) + ${PREVIEW_BANNER_H}px)`
          : 'env(safe-area-inset-top, 0px)',
        left: 0,
        right: 0,
        zIndex: 800,
        background: 'rgba(var(--bg-rgb), 0.92)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 var(--space-3)',
        height: 36,
        gap: 4,
        // Horizontal clipping contains the crumb-truncation ellipsis.
        //
        // The 'visible' below DOES NOT DO WHAT IT SAYS, and this comment
        // used to claim it did. CSS coerces overflow-y to 'auto' whenever
        // overflow-x computes to 'hidden', so the used value here is
        // 'auto' (measured with getComputedStyle) and this nav clips its
        // children at its 35px content box. The 44px controls inside are
        // therefore about 35px of reachable height, not 44.
        //
        // Left as-is rather than half-fixed: dropping the horizontal
        // clipping to buy back the vertical would let the breadcrumb trail
        // run off the side instead of ellipsizing, which is a worse bug on
        // the 320px screens the trail was already tuned for. Fixing both
        // at once is real work and is tracked in TASK-QUEUE.md.
        overflowX: 'hidden',
        overflowY: 'visible',
      }}
    >
      {showHamburger && <HamburgerButton open={menuOpen} onToggle={onMenuToggle} />}
      {/* Search leads every other control on the strip - right after the
          hamburger, ahead of the breadcrumb trail and the theme toggle -
          on the minimal home variant and the full variant alike. */}
      {!onSearchPage && <HeaderSearchButton />}
      {onHome ? null : (
        <>
          {/* Home is always crumbs[0] (see buildCrumbs) - pulled out into
              its own button rather than mapped as a trail entry, per
              HomeCrumbButton's doc comment above. The rest of the trail
              (crumbs[1..]) still maps normally, each one preceded by a
              "›" separator since Home no longer supplies the first one. */}
          <HomeCrumbButton />
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, overflow: 'hidden' }}>
            {crumbs.slice(1).map((c, sliceIdx) => {
              const isLast = sliceIdx === crumbs.length - 2;
              return (
                <span
                  key={c.href + sliceIdx}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    // Every crumb shrinks now, not just the last one - see
                    // the 320px note below. minWidth 32 is a visible-text
                    // floor: enough room for a few letters plus the ellipsis
                    // rather than a crumb getting squeezed to nothing.
                    minWidth: 32,
                    flexShrink: 1,
                    overflow: 'hidden',
                  }}
                >
                  <span style={{ fontSize: 11, color: 'var(--ink-soft)', flexShrink: 0 }}>›</span>
                  {isLast ? (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: 'var(--ink)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {c.label}
                    </span>
                  ) : (
                    // 320px note: this used to be flexShrink 0, so on a
                    // route with a long parent crumb ("Comp/Recital Tracker")
                    // it never gave an inch, and the current-page crumb
                    // after it got squeezed to 0px wide - fully invisible,
                    // not just truncated. Measured with a Playwright
                    // reproduction of this exact markup at 320px before
                    // this fix. Parent crumbs now shrink and ellipsize too,
                    // so worst case both read as partial text ("Produc..."
                    // "Costume Lab...") instead of one vanishing outright.
                    <a
                      href={c.href}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--ink-soft)',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                      }}
                    >
                      {c.label}
                    </a>
                  )}
                </span>
              );
            })}
          </div>
          <ThemeChip />
        </>
      )}
    </nav>
  );
}

/**
 * Compact light/dark toggle that rides in the breadcrumb strip, so the
 * theme switch is reachable from every page (the full labeled toggle
 * stays on the home screen).
 *
 * Same outer-box-and-inner-pill shape as HeaderSearchButton, for one
 * measured reason: this chip was PAINTING a defect. It asked for a 28x28
 * circle, but index.css floors every button at min-height:
 * var(--tap-target) and an inline height: 28 does not beat a min-height,
 * so the chip drew as a 28x44 capsule with its top clipped off. Moving the
 * fill into an inner span gives the chip its circle back, verified light
 * and dark at 390 and 1280.
 *
 * Its HIT BOX also went from 28 to 44px wide, which is a real gain because
 * the strip's clipping is vertical only. Its reachable HEIGHT is about
 * 35px, before and after, same as its three neighbours - see
 * stripTapTargetStyle. This is not the fix for that.
 */
function ThemeChip() {
  const { theme, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        ...stripTapTargetStyle,
        width: 44,
        border: 'none',
        background: 'transparent',
        boxShadow: 'none',
        cursor: 'pointer',
        // See HamburgerButton: longhands so the shared bottom padding that
        // centers the chip on the row survives.
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 'var(--radius-pill)',
          color: 'var(--ink-soft)',
          background: 'var(--silver-mist)',
          border: '1px solid var(--silver-edge)',
        }}
      >
        <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Back FAB
// ---------------------------------------------------------------------------

// position: 'absolute' here is deliberate, not a typo - see the
// .ldg-fab-layer comment in index.css. These FABs are rendered inside that
// layer (a position:fixed, bottom:0-anchored wrapper), so 'absolute' still
// anchors them to a viewport-stable box; only the wrapper needs 'fixed'.
const fabBase: React.CSSProperties = {
  position: 'absolute',
  zIndex: 900,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  minHeight: 44,
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  cursor: 'pointer',
  fontFamily: 'var(--font-body)',
  fontWeight: 800,
  fontSize: 14,
  boxShadow: '0 10px 24px rgba(35,28,20,0.12), inset 0 1px 0 rgba(255,255,255,0.62)',
};

/**
 * Exported (2026-09-17) so tests/harness/website-editor-phone-ui can mount
 * the REAL Back FAB next to the website editor's pinned Save bar, rather
 * than a hand-rebuilt stand-in, to prove nothing floats over it. Behavior
 * unchanged; this is a visibility change only.
 */
export function BackButton({ crumbs }: { crumbs: Crumb[] }) {
  const navigate = useNavigate();
  if (crumbs.length <= 1) return null;

  const href = backHref(crumbs);

  return (
    <button
      type="button"
      aria-label="Go back"
      onClick={() => {
        // This is genuinely a back button, not a "go home" button - it
        // replays browser history (preserving scroll position), landing
        // wherever the person actually came from. Bobbie, testing on her
        // phone, read the old crumb-derived label ("Home", "Teach",
        // "Calendar"...) as a promise of a fixed destination; on a
        // non-linear path (a notification deep link, a jump in from
        // search) history back does not necessarily go there. The label
        // is plain "Back" so it never claims more than it does. The
        // parent-breadcrumb href is only a fallback for the rare case of
        // no history at all (a fresh tab, a deep link with nothing behind
        // it in this browsing session).
        if (window.history.length > 1) navigate(-1);
        else navigate(href);
      }}
      style={{
        ...fabBase,
        left: 'calc(env(safe-area-inset-left, 0px) + 14px)',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)',
        padding: '0 18px 0 14px',
        background: 'linear-gradient(180deg, var(--paper) 0%, var(--off-white) 100%)',
        color: 'var(--ink)',
        border: '1px solid var(--silver-edge)',
        maxWidth: 160,
        overflow: 'hidden',
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1, marginTop: -2, flexShrink: 0 }}>‹</span>
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        Back
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Jump-to-top FAB
// ---------------------------------------------------------------------------

/** Exported for the same reason as BackButton above. */
export function JumpToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    // Some pages scroll an inner container instead of the window body, and
    // short pages never reach a deep scrollY. Show the FAB either when the
    // window itself has scrolled a little, OR when the document is tall
    // enough that it's meaningfully scrollable at all — that way the FAB
    // still appears once someone scrolls an inner panel that grows the
    // document height, even if window.scrollY itself stays near 0.
    const check = () => {
      const scrolledWindow = window.scrollY > 200;
      const pageIsLong =
        document.documentElement.scrollHeight - window.innerHeight > 600;
      setShow(scrolledWindow || pageIsLong);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);
  if (!show) return null;
  return (
    <button
      type="button"
      aria-label="Jump to top"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      style={{
        ...fabBase,
        right: 'calc(env(safe-area-inset-right, 0px) + 14px)',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)',
        width: 48,
        height: 48,
        padding: 0,
        background: 'linear-gradient(180deg, var(--paper) 0%, var(--off-white) 100%)',
        color: 'var(--lime-deep)',
        border: '1px solid var(--silver-edge)',
      }}
    >
      <span style={{ fontSize: 22, lineHeight: 1, marginTop: -2 }}>↑</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// "View as" preview banner
// ---------------------------------------------------------------------------

/**
 * Persistent top bar shown while an active Director is previewing another
 * role. Makes the impersonation impossible to miss and gives a one-tap way
 * out. Rendered only when a preview role is active (useUserDoc gates that
 * on canPreviewRole: real role Director, real status active - see
 * lib/previewRole.ts), so nothing shows for normal use. Works on any host
 * now, not only the demo/staging hosts.
 *
 * The second line states the actual limit in plain words (packet J3, build
 * step 4): this is the Director's OWN data, shown through another role's
 * screens, never another person's data. Kept short enough to hold to one
 * line down to a 320px phone; PREVIEW_BANNER_H is sized for these two rows.
 */
function PreviewBanner() {
  const { user } = useAuth();
  const previewRole = usePreviewRole(user?.uid ?? null);
  const navigate = useNavigate();
  if (!previewRole) return null;
  const label = PREVIEW_ROLE_LABEL[previewRole];
  return (
    <div
      role="status"
      // Same reason as the breadcrumb strip: fixed chrome that would
      // otherwise print over the top of the page. A preview banner on a
      // printed roster is noise at best and confusing at worst.
      className="no-print"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 900,
        minHeight: PREVIEW_BANNER_H,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 6,
        paddingInline: 'var(--space-3)',
        /* Flat --ink, not a gradient into --ink-2. --ink-2 is an ALPHA token
           (it has to be, so one value works on a card and on the page wash),
           and a translucent stop here let the page show through the right-hand
           end of the banner, dragging the "Leave preview" control down toward
           the contrast floor. This is a fixed bar over arbitrary content, so it
           needs to be opaque. Flat --ink against --paper is 18.84:1 in light
           and 16.43:1 in dark. */
        background: 'var(--ink)',
        color: 'var(--paper)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-3)',
          fontSize: 13,
          fontWeight: 800,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="people" size={16} /> Viewing as {label}
        </span>
        <button
          type="button"
          onClick={() => {
            setPreviewRole(null);
            navigate('/manage-users', { replace: true });
          }}
          style={{
            minHeight: 30,
            borderRadius: 'var(--radius-pill)',
            border: '1px solid rgba(255,255,255,0.6)',
            background: 'transparent',
            color: 'var(--paper)',
            fontWeight: 800,
            fontSize: 12,
            padding: '0 14px',
            cursor: 'pointer',
          }}
        >
          Leave preview
        </button>
      </div>
      <span style={{ fontSize: 10.5, fontWeight: 600, opacity: 0.85, textAlign: 'center' }}>
        Your own data, not another person's data.
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export default function GlobalChrome() {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const previewRole = usePreviewRole(user?.uid ?? null);
  const { userDoc } = useUserDoc();
  const mustChangePassword = useMustChangePassword(userDoc?.mustChangePassword);
  const rawCrumbs = buildCrumbs(location.pathname);
  // /attendance is teaching-staff only (2026-08-15 ruling) while the report
  // and concerns pages under it admit every staff role (2026-09-01). For a
  // viewer the marking page would lock out - Admin - the middle Attendance
  // crumb (and the Back fallback derived from it) was a door straight onto
  // its lock screen (Fable review on PR #820). Drop that middle crumb for
  // them; the crumb for the page they are ON always stays.
  const crumbs =
    userDoc?.status === 'active' && !canOpenAttendancePage(userDoc.role)
      ? rawCrumbs.filter(
          (c, i) => !(c.href === '/attendance' && i < rawCrumbs.length - 1)
        )
      : rawCrumbs;
  const [menuOpen, setMenuOpen] = useState(false);
  const [passwordGateDismissed, setPasswordGateDismissed] = useState(false);
  const fabsHidden = useGlobalFabsHidden();
  // Nudges every .ldg-fab-layer consumer (this file's own Back/jump-to-top
  // pair, AssistantLauncher's bubble, FreshnessPill) up off the keyboard,
  // on top of the CSS bottom:0 anchor that already puts them in the right
  // corner with no JS at all - see fabViewportTracking.ts for the four
  // real-device reports this answers.
  useFabViewportTracking();
  // After the keyboard closes, asks iOS to line the fixed frame back up
  // with the screen if it is still offset (2026-10-03 report). Places
  // nothing: it requests the current scroll position, and on a device the
  // content may shift to meet the frame. See viewportResync.ts.
  useViewportResync();
  // The same active-Director check every page uses, on the role the app is
  // rendering as, so a Director in "view as" preview sees what that role
  // sees. Gates only the TEMPORARY screen check below.
  const isDirector = userDoc?.status === 'active' && userDoc.role === 'director';

  useEffect(() => {
    setPasswordGateDismissed(false);
  }, [user?.uid]);

  // The hamburger only exists for signed-in people (there's no menu to show
  // a signed-out visitor). On every page other than home the strip shows
  // regardless, same as before; on home it now shows too, but only to open
  // the drawer — see BreadcrumbStrip's onHome branch.
  // Keep the hamburger through auth rehydration (stability audit, finding
  // 2): on a resumed iOS home-screen app or a cold Safari start, `user` is
  // null for as long as IndexedDB takes to hand the session back, and
  // hiding the whole strip during that window read as "the menu is gone".
  // A genuinely signed-out visitor still sees no hamburger once auth
  // settles, and the drawer itself renders nothing without a user.
  const showHamburger = !!user || authLoading;
  // True once the menu drawer's chunk has been fetched (see loadNavDrawer),
  // or once a tap has put the drawer in the tree. The second half matters
  // when the fetch ahead of need failed and the tap's own import then loaded
  // (a browser that retries a failed import, after the signal came back):
  // without it the drawer was in the tree only while the menu was open, so
  // closing it unmounted it in the same render, its marked history entry was
  // never popped, and a link tap, which waits for that pop, went nowhere
  // (refute round, 2026-10-25; cases U and V in the nav-drawer harness).
  const [navDrawerReady, setNavDrawerReady] = useState(false);
  if (menuOpen && !navDrawerReady) setNavDrawerReady(true);
  useEffect(() => {
    if (!showHamburger || navDrawerReady) return;
    let live = true;
    void preloadChunk(loadNavDrawer).then((ok) => {
      if (live && ok) setNavDrawerReady(true);
    });
    return () => {
      live = false;
    };
  }, [showHamburger, navDrawerReady]);
  const onHome = crumbs.length <= 1;
  const stripShowing = !onHome || showHamburger;

  // A real-family preview is a temporary look at a small, deliberately-wired
  // set of family screens, never a mode for the rest of the Hub. Clear it the
  // moment this tab leaves that set, and on pagehide so a restored iPhone tab
  // cannot reopen yesterday's family. This is also how "exit preview by
  // tapping into a screen preview does not reach" works: a class page or a
  // dancer profile is not on this list, so opening one from a previewed
  // family screen clears the preview on the way in instead of showing the
  // Director's own data/permissions under the family's name.
  useEffect(() => {
    if (!FAMILY_PREVIEW_ROUTES.has(location.pathname)) setPreviewedFamily(null);
  }, [location.pathname]);

  useEffect(() => {
    const clearFamilyPreview = () => setPreviewedFamily(null);
    window.addEventListener('pagehide', clearFamilyPreview);
    return () => window.removeEventListener('pagehide', clearFamilyPreview);
  }, []);

  // The breadcrumb strip and the preview banner are BOTH position:fixed, so
  // they overlay the top of page content and clip headings/loading text.
  // Reserve body top space equal to whatever fixed chrome is showing so every
  // page starts below it. Fixed elements ignore body padding, so the chrome
  // stays put while the app body shifts down under it. Now that the strip
  // shows on home too (for signed-in people, as just the hamburger), this
  // reservation follows stripShowing instead of the old crumbs-only check.
  //
  // Same idea at the bottom: the back FAB and jump-to-top FAB are also
  // position:fixed and float over whatever the page renders last, whether
  // that is plain end-of-page content (a page scrolled all the way down has
  // its last button sitting right at the viewport edge, under the FAB) or a
  // sticky bottom bar (a sticky bottom:0 element measures its stuck position
  // from the bottom of the scrollport, which body padding-bottom shrinks).
  // Reserving FAB_CLEARANCE_H at the bottom of ordinary pages pushes both
  // cases above the FAB band. Full-viewport screens that explicitly hide the
  // FABs keep only the device safe-area inset, so a conversation composer or
  // schedule grid does not inherit a blank 140px band.
  useEffect(() => {
    const topOffset = (previewRole ? PREVIEW_BANNER_H : 0) + (stripShowing ? BREADCRUMB_H : 0);
    document.documentElement.style.setProperty('--hub-chrome-top', `${topOffset}px`);
    document.documentElement.style.setProperty('--hub-chrome-bottom', `${fabsHidden ? 0 : FAB_CLEARANCE_H}px`);
    document.body.style.paddingTop = topOffset
      ? `calc(env(safe-area-inset-top, 0px) + ${topOffset}px)`
      : '';
    document.body.style.paddingBottom = fabsHidden
      ? 'env(safe-area-inset-bottom, 0px)'
      : `calc(env(safe-area-inset-bottom, 0px) + ${FAB_CLEARANCE_H}px)`;
    return () => {
      document.body.style.paddingTop = '';
      document.body.style.paddingBottom = '';
      document.documentElement.style.removeProperty('--hub-chrome-top');
      document.documentElement.style.removeProperty('--hub-chrome-bottom');
    };
  }, [previewRole, stripShowing, fabsHidden]);

  // Close the drawer automatically if the person signs out from within it.
  useEffect(() => {
    if (!user) setMenuOpen(false);
  }, [user]);

  return (
    <>
      <ForcePasswordOverlay
        mustChange={mustChangePassword}
        onDone={() => setPasswordGateDismissed(true)}
      />
      <WelcomeTour
        viewerKey={user ? `adult:${user.uid}` : null}
        role={userDoc?.status === 'active' ? userDoc.role : null}
        ready={
          !!user &&
          userDoc?.status === 'active' &&
          userDoc.notifPrefs !== undefined &&
          (!mustChangePassword || passwordGateDismissed) &&
          // Never over a costume pickup page (2026-09-25): a new parent who
          // signs in at the desk to mark her bag had the first-run tour
          // covering the button until she found Skip. It waits for home.
          !isCostumePickupPath(location.pathname)
        }
      />
      <PreviewBanner />
      <BreadcrumbStrip
        crumbs={crumbs}
        pathname={location.pathname}
        previewActive={!!previewRole}
        showHamburger={showHamburger}
        menuOpen={menuOpen}
        onMenuToggle={() => setMenuOpen((v) => !v)}
      />
      {/* Only where the menu button can show (signed in, or sign-in still
          restoring): a signed-out visitor has no menu, so never downloads it.
          In the tree once its chunk has landed or a tap has mounted it, and
          then it stays (navDrawerReady). */}
      {((showHamburger && navDrawerReady) || menuOpen) && (
        <OptionalFeatureBoundary feature="NavDrawer">
          <Suspense fallback={null}>
            <NavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
          </Suspense>
        </OptionalFeatureBoundary>
      )}
      {/* Shared viewport-stable layer - see .ldg-fab-layer in index.css.
          zIndex 900 matches these two FABs' own prior fixed z-index, so
          their ranking against NavDrawer/PreviewBanner/etc is unchanged.
          Suppressed while the schedule grid's full screen mode is active on
          a page with no room to spare for them (see globalFabVisibility.ts)
          - the grid supplies its own Exit affordance, so nothing is lost. */}
      {!fabsHidden && (
        <div className="ldg-fab-layer" style={{ zIndex: 900 }}>
          <BackButton crumbs={crumbs} />
          <JumpToTop />
          {/* TEMPORARY, Director only: remove with ScreenCheckReadout.tsx
              once the 2026-10-03 cause is confirmed on Bobbie's phone.
              Inside this layer so it is on screen wherever the layer ended
              up. A Director can also open it with ?screencheck=1. */}
          {isDirector && <ScreenCheckReadout />}
        </div>
      )}
      {!fabsHidden && <AssistantLauncher />}
      {/* StillSendingBanner ("Uploads say they are sending...", Packet R24)
          mounts in App.tsx now, not here - R24 review, MEDIUM 5: this
          component is suppressed entirely for a dancer-only session, and a
          dancer session needs the same upload visibility an adult gets. */}
      <FreshnessPill />
    </>
  );
}

/**
 * Version-freshness pill (task #125; conditional auto-update 2026-08-02).
 *
 * useVersionFreshness checks /version.json on foreground, on route change and
 * from bfcache. When this tab is out of date it now updates ITSELF, silently,
 * as long as there is provably nothing to lose. This pill is the fallback for
 * the other case: something is unsaved, an upload is in flight, a dialog is
 * open, or a previous automatic attempt did not take. It is also the way
 * forward when a part of the Hub loaded on demand could not load its file
 * after an update while something was unsaved: lib/lazyRoute.ts holds that
 * reload for this pill rather than reload over the work. So `prompt` rather than
 * `stale` is the right signal to render on - a stale tab that is about to
 * reload should not flash a pill on the way out.
 */
function FreshnessPill() {
  const location = useLocation();
  const { prompt } = useVersionFreshness(location.pathname);
  // While this pill is mounted it is the way forward for a held stale-chunk
  // reload (lib/reloadGuard.ts): lazyRoute.ts only holds one, rather than
  // reloading over unsaved work, when something can offer the reload.
  useEffect(() => offerHeldReload(), []);
  if (!prompt) return null;
  // Wrapped in .ldg-fab-layer (see index.css) so 'absolute' below anchors
  // against a viewport-stable box instead of the animatable layout
  // viewport - same fix as the other floating chrome, own layer because
  // this pill's z-index (1300) needs to keep outranking the nav drawer
  // independently of the Home/jump-to-top pair below.
  return (
    <div className="ldg-fab-layer" style={{ zIndex: 1300 }}>
      <div
        role="status"
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
          zIndex: 1300,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          borderRadius: 'var(--radius-pill)',
          border: '1px solid var(--line)',
          background: 'var(--paper)',
          color: 'var(--ink)',
          boxShadow: '0 10px 26px rgba(35,28,20,0.22)',
          fontSize: 13,
          fontWeight: 700,
          maxWidth: 'calc(100vw - 24px)',
        }}
      >
        {/* Says why it is asking instead of just doing it: the only way to
            see this pill is to have something the Hub will not throw away. */}
        <span>An update is ready. Finish up and tap Reload.</span>
        <button
          type="button"
          onClick={() => {
            // Same shared cooldown the automatic paths stamp, so a manual
            // reload cannot be immediately followed by an automatic one.
            markReload();
            window.location.reload();
          }}
          style={{
            minHeight: 32,
            padding: '0 12px',
            border: 0,
            borderRadius: 'var(--radius-pill)',
            background: 'var(--lime)',
            color: '#0E1A0A',
            fontWeight: 800,
            fontSize: 13,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

/**
 * Route-wide enforcement of the forced first-login password change (review
 * P1-2). Landing gates its own card, but Landing is only the "/" route - a
 * deep link (push notification, typed URL) landed a temp-password account
 * anywhere in the app with the studio-wide starter password still set. This
 * overlay renders on EVERY route while users/{me}.mustChangePassword is
 * true, so the gate is inescapable, matching what the ship claimed.
 *
 * DELIBERATELY NOT `.no-print`, unlike every other fixed thing in this file.
 * Hiding it on paper would print the very content it exists to withhold,
 * uncovered.
 *
 * Do not upgrade that into a claim it cannot carry. An adversarial reviewer
 * measured it 2026-08-06: with the overlay up, all 150 underlying text-show
 * operations are STILL in the generated PDF, and under Chromium's default
 * printBackground:false the overlay's own background is not painted at all,
 * so the gated page prints through the gate today. Leaving the overlay
 * visible does not withhold anything; it only declines to make the leak
 * tidier. The decision stands, the guarantee does not exist, and the real
 * gap - a mustChangePassword account printing a gated route - is pre-existing
 * and still open.
 */
function ForcePasswordOverlay({
  mustChange,
  onDone,
}: {
  mustChange: boolean;
  onDone: () => void;
}) {
  // Escape hatch: once the person finishes the change, Continue dismisses
  // the gate for this session even if the server-side claim clear hiccuped.
  // Without this, a single failed function call trapped them behind the
  // sheet forever (stability audit, finding 1C).
  const [dismissed, setDismissed] = useState(false);
  if (!mustChange || dismissed) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'var(--bg)',
        overflowY: 'auto',
        padding:
          'calc(env(safe-area-inset-top, 0px) + var(--space-6)) var(--space-4) calc(env(safe-area-inset-bottom, 0px) + var(--space-6))',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <ForcePasswordChange
        onDone={() => {
          setDismissed(true);
          onDone();
        }}
      />
    </div>
  );
}
